import { observable, computed, action, makeObservable } from 'mobx'
import Toggle from 'infra/Toggle'
import NotificationStore from 'notifications/NotificationStore'
import { Observable } from 'rxjs'
import Member from 'iam/models/Member'
import Workspace from 'iam/models/Workspace'
import NotificationListViewModel from '../NotificationList/NotificationListViewModel'
import { first } from 'rxjs/operators'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'

/**
 * Notifications sidebar view model.
 */
export default class NotificationsSidebarViewModel {
  /**
   * The workspace to filter by. When `null`, shows notifications for all workspaces (default).
   */
  @observable private internalSelectedWorkspace = Workspace.none

  /**
   * Whether the sidebar is showing or not.
   */
  showing = new Toggle(false)

  /**
   * Notification list view models by workspace, including `Workspace.none`.
   */
  lists = new Map<Workspace, NotificationListViewModel>()

  constructor(
    private notificationStore: NotificationStore,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private currentMember$: Observable<Member>,
    private getTime: () => Date,
  ) {
    makeObservable(this)
    this.selectWorkspace(Workspace.none)
  }

  /**
   * Activates the view model
   */
  activate() {
    // Activation of lists are done by the NotificationList component, as it might be hosted elsewhere.
  }

  /**
   * Deactivates the view model
   */
  deactivate() {
    //
  }

  /**
   * The notification list view model.
   */
  @computed
  get list() {
    let list = this.lists.get(this.selectedWorkspace)
    if (!list) {
      list = this.createListViewModel()
      this.lists.set(this.selectedWorkspace, list)
    }
    return list
  }

  /**
   * Available workspaces.
   */
  @computed
  get workspaces() {
    return this.getCurrentUserWorkspaceMemberships().map((m) => m.workspace)
  }

  /**
   * The workspace being filtered on, uses the first one if there's only one available.
   */
  @computed
  get selectedWorkspace(): Workspace {
    return this.workspaces.length === 1
      ? this.workspaces[0]
      : this.internalSelectedWorkspace
  }

  /**
   * Selects a workspace.
   */
  @action.bound
  selectWorkspace(workspace: Workspace) {
    this.internalSelectedWorkspace = workspace
    let list = this.lists.get(this.selectedWorkspace)
    if (!list) {
      list = this.createListViewModel()
      this.lists.set(this.selectedWorkspace, list)
    }
  }

  /**
   * Creates a list view model.
   */
  private createListViewModel(): NotificationListViewModel {
    return new NotificationListViewModel(
      this.selectedWorkspace,
      this.notificationStore,
      this.getTime,
      // When a notification is interacted with (clicked), hide the sidebar.
      this.showing.disable,
      // Used to fetch the initial notifications.
      // We only want to do that once per list view model.
      this.currentMember$.pipe(first()),
    )
  }
}
